import SEO from "../components/SEO";
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTheme, useAuth, API } from "../App";
import axios from "axios";
import { formatNZDate, formatNZDateLong } from "../utils/timezone";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, Building2, Phone, MapPin, Star, Clock,
  MessageSquare, CheckCircle, Globe, Calendar, Sun, Moon, Lock
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

export default function ShopPublicProfile() {
  const { shop_id } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shopData, setShopData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShopProfile = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/shop-profile/${shop_id}`, { headers });
        setShopData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Shop not found");
        toast.error("Failed to load shop profile");
      } finally {
        setLoading(false);
      }
    };

    fetchShopProfile();
  }, [shop_id, token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !shopData) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/">
              <img
                src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL}
                alt="TyreMatch"
                className="h-10"
              />
            </Link>
          </div>
        </header>
        <main className="container mx-auto px-4 py-12 text-center">
          <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-50" />
          <h1 className="text-2xl font-bold mb-2">Shop Not Found</h1>
          <p className="text-muted-foreground mb-6">{error || "This shop profile is not available."}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </main>
      </div>
    );
  }

  const { shop, reviews, stats } = shopData;

  return (
    <div className="min-h-screen bg-background" data-testid="shop-public-profile">
      <SEO page="shopProfile" customTitle={`${shop.shop_name} | TyreMatch`} customDescription={`View ${shop.shop_name}'s profile, reviews, and ratings. Book tyre services with confidence on TyreMatch.`} />
      
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Link to="/">
              <img
                src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL}
                alt="TyreMatch"
                className="h-10"
              />
            </Link>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Shop Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold" data-testid="shop-name">{shop.shop_name}</h1>
              <p className="text-muted-foreground">Verified TyreMatch Partner</p>
              {shop.google_rating != null && (
                <div className="flex items-center gap-2 mt-2" data-testid="shop-google-rating">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                  <span className="font-semibold">{shop.google_rating.toFixed(1)}</span>
                  {shop.google_review_count != null && (
                    <span className="text-muted-foreground">({shop.google_review_count} reviews)</span>
                  )}
                </div>
              )}
            </div>
            
            {/* Rating Summary */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-5 w-5 ${
                      star <= Math.round(stats.avg_rating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="font-semibold">{stats.avg_rating.toFixed(1)}</span>
              <span className="text-muted-foreground">({stats.total_reviews} reviews)</span>
            </div>
          </div>

          {/* Stats Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Verified Partner
            </Badge>
            <Badge variant="secondary" className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {stats.completed_orders} Jobs Completed
            </Badge>
            <Badge variant="secondary" className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {shop.service_radius_km} km Service Area
            </Badge>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Contact Information - Left Column */}
          <div className="md:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="h-4 w-4" />
                  Shop Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Contact details intentionally hidden — TyreMatch is a quote-and-book
                    platform; customers contact shops post-booking. */}
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <Lock className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Contact details are available after booking confirmation
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium" data-testid="shop-address">
                      {shop.address?.suburb && shop.address?.city 
                        ? `${shop.address.suburb}, ${shop.address.city}` 
                        : shop.address?.city || shop.address?.full_address || "N/A"}
                    </p>
                  </div>
                </div>

                {shop.google_maps_url && (
                  <a
                    href={shop.google_maps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 w-full"
                  >
                    <Button variant="outline" className="w-full" data-testid="view-on-maps-btn">
                      <Globe className="h-4 w-4 mr-2" />
                      View on Google Maps
                    </Button>
                  </a>
                )}

                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <p className="text-sm text-muted-foreground">Member Since</p>
                    <p className="font-medium">
                      {shop.join_date ? formatNZDateLong(shop.join_date) : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reviews - Right Column */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  Customer Reviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Rating Summary */}
                <div className="flex items-center gap-6 mb-6 p-4 bg-muted/50 rounded-lg">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-primary">{stats.avg_rating.toFixed(1)}</p>
                    <div className="flex items-center justify-center gap-0.5 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${
                            star <= Math.round(stats.avg_rating)
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{stats.total_reviews} reviews</p>
                  </div>
                </div>

                {/* Reviews List */}
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No reviews yet</p>
                      <p className="text-sm">Be the first to leave a review!</p>
                    </div>
                  ) : (
                    reviews.map((review, index) => (
                      <div key={review.review_id || `review-${index}`} className="border rounded-lg p-4" data-testid={`review-${index}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= review.rating
                                    ? "fill-yellow-400 text-yellow-400"
                                    : "text-gray-300"
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {review.created_at 
                              ? formatNZDate(review.created_at)
                              : ""
                            }
                          </span>
                        </div>
                        {review.comments ? (
                          <p className="text-foreground">{review.comments}</p>
                        ) : (
                          <p className="text-muted-foreground italic text-sm">No comment provided</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12 py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>© 2026 TyreMatch. New Zealand's smartest way to compare tyre quotes and book services.</p>
        </div>
      </footer>
    </div>
  );
}
